(() => {
  const DENSITY_KEY = "vernika:density";
  class Listing {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.timer = null;
      this.controller = null;
      this.extras = () => [ ...document.querySelectorAll(`[data-listing-for="${this.sectionId}"]`) ];
      root.addEventListener("change", (event => this.onChange(event)));
      root.addEventListener("input", (event => {
        if (event.target.matches("[data-price-input]")) this.queue(event.target.form, 900);
      }));
      root.addEventListener("submit", (event => {
        if (event.target.matches("[data-facets-form], [data-sort-form]")) {
          event.preventDefault();
          this.queue(event.target, 0);
        }
      }));
      document.addEventListener("click", (event => this.onClick(event)));
      window.addEventListener("popstate", (() => this.render(window.location.pathname + window.location.search, false)));
      this.applyDensity(window.Vernika?.store?.get(DENSITY_KEY, null));
      root.querySelectorAll("[data-sort]").forEach((wrap => this.setupSort(wrap)));
      this.initStream();
    }
    initStream() {
      this.stream = null;
      const grid = this.root.querySelector('[data-region="grid"] .product-grid[data-hold-sold]');
      if (!grid) return;
      grid.querySelectorAll(":scope > li[data-sold]").forEach((item => item.remove()));
      const perPage = Number(grid.dataset.perPage) || 24;
      const seen = new Set([ ...grid.querySelectorAll(":scope > li[data-key]") ].map((item => item.dataset.key)));
      this.stream = {
        grid: grid,
        perPage: perPage,
        total: Number(grid.dataset.total) || 0,
        mode: "1",
        page: Math.floor(seen.size / perPage) + 1,
        seen: seen,
        buffer: []
      };
      if (seen.size === 0) {
        const link = this.root.querySelector('[data-region="pagination"] [data-load-more]');
        if (link) this.loadStream(link);
      }
    }
    async loadStream(link) {
      const s = this.stream;
      if (s.busy) return;
      s.busy = true;
      link.setAttribute("aria-busy", "true");
      const before = s.grid.children.length;
      let added = 0;
      const take = () => {
        while (added < s.perPage && s.buffer.length) {
          s.grid.append(s.buffer.shift());
          added += 1;
        }
      };
      try {
        take();
        while (added < s.perPage && s.mode) {
          const url = new URL(window.location.href);
          url.searchParams.delete("page");
          url.searchParams.delete("filter.v.availability");
          url.searchParams.set("filter.v.availability", s.mode);
          url.searchParams.set("page", String(s.page));
          const doc = await this.fetchSection(url.pathname + url.search);
          const items = [ ...doc.querySelectorAll('[data-region="grid"] .product-grid > li[data-key]') ];
          items.forEach((item => {
            if (s.mode === "1" === item.hasAttribute("data-sold")) return;
            if (s.seen.has(item.dataset.key)) return;
            s.seen.add(item.dataset.key);
            s.buffer.push(document.importNode(item, true));
          }));
          const hasNext = doc.querySelector('[data-region="pagination"] [data-load-more]');
          if (hasNext && items.length) {
            s.page += 1;
          } else if (s.mode === "1") {
            s.mode = "0";
            s.page = 1;
          } else {
            s.mode = null;
          }
          take();
        }
        this.showProgress(link);
        window.Vernika?.initReveal?.(this.root);
        document.dispatchEvent(new CustomEvent("listing:updated"));
        s.grid.children[before]?.querySelector("a")?.focus({
          preventScroll: true
        });
      } catch (error) {
        if (error.name !== "AbortError") window.location.href = link.href;
      } finally {
        s.busy = false;
        link.removeAttribute("aria-busy");
      }
    }
    showProgress(link) {
      const s = this.stream;
      const shown = s.grid.children.length;
      const total = Math.max(s.total, shown);
      const box = link.closest(".load-more");
      const text = box?.querySelector("[data-showing]");
      if (text) text.textContent = text.dataset.showing.replace("[S]", String(shown));
      box?.querySelector(".load-more__track span")?.style.setProperty("--progress", `${Math.round(shown * 100 / total)}%`);
      if (!s.mode && !s.buffer.length) link.remove();
    }
    setupSort(wrap) {
      const select = wrap.querySelector("[data-sort-select]");
      const button = wrap.querySelector("[data-sort-button]");
      const menu = wrap.querySelector("[data-sort-menu]");
      const current = wrap.querySelector("[data-sort-current]");
      if (!select || !button || !menu) return;
      wrap.classList.add("is-enhanced");
      const options = () => [ ...menu.querySelectorAll('[role="option"]') ];
      const close = focusButton => {
        if (menu.hidden) return;
        menu.hidden = true;
        button.setAttribute("aria-expanded", "false");
        if (focusButton) button.focus();
      };
      const open = () => {
        menu.hidden = false;
        button.setAttribute("aria-expanded", "true");
        const list = options();
        (list.find((o => o.getAttribute("aria-selected") === "true")) || list[0])?.focus();
      };
      this.markSort = value => {
        options().forEach((o => {
          const on = o.dataset.value === value;
          o.setAttribute("aria-selected", String(on));
          if (on && current) current.textContent = o.textContent.trim();
        }));
      };
      const choose = option => {
        this.markSort(option.dataset.value);
        close(true);
        if (select.value !== option.dataset.value) {
          select.value = option.dataset.value;
          select.dispatchEvent(new Event("change", {
            bubbles: true
          }));
        }
      };
      button.addEventListener("click", (() => menu.hidden ? open() : close(false)));
      button.addEventListener("keydown", (event => {
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          open();
        }
      }));
      menu.addEventListener("click", (event => {
        const option = event.target.closest('[role="option"]');
        if (option) choose(option);
      }));
      menu.addEventListener("keydown", (event => {
        const list = options();
        const i = list.indexOf(document.activeElement);
        if (event.key === "ArrowDown") list[Math.min(i + 1, list.length - 1)]?.focus(); else if (event.key === "ArrowUp") list[Math.max(i - 1, 0)]?.focus(); else if (event.key === "Home") list[0]?.focus(); else if (event.key === "End") list[list.length - 1]?.focus(); else if (event.key === "Enter" || event.key === " ") {
          if (list[i]) choose(list[i]);
        } else if (event.key === "Escape") close(true); else if (event.key === "Tab") close(false); else return;
        if (event.key !== "Tab") event.preventDefault();
      }));
      document.addEventListener("click", (event => {
        if (!wrap.contains(event.target)) close(false);
      }));
    }
    syncChips() {
      const here = new URL(window.location.href);
      const type = here.searchParams.get("filter.p.product_type");
      this.extras().forEach((box => {
        const chips = [ ...box.querySelectorAll("[data-chip]") ];
        const match = chips.find((chip => {
          const url = new URL(chip.href, here);
          return url.pathname === here.pathname && url.searchParams.get("filter.p.product_type") === type;
        })) || chips[0];
        chips.forEach((chip => {
          const on = chip === match;
          chip.classList.toggle("is-on", on);
          if (on) chip.setAttribute("aria-current", "page"); else chip.removeAttribute("aria-current");
        }));
      }));
    }
    onChange(event) {
      const target = event.target;
      if (target.matches("[data-sort-select]")) {
        this.queue(null, 0);
      } else if (target.closest("[data-facets-form]") && !target.matches("[data-price-input]")) {
        this.queue(target.form, 250);
      }
    }
    onClick(event) {
      const inExtras = this.extras().some((box => box.contains(event.target)));
      if (!this.root.contains(event.target) && !inExtras && !event.target.closest(`#filters-${this.sectionId}`)) return;
      const link = event.target.closest("[data-facet-link]");
      if (link) {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
        event.preventDefault();
        const url = new URL(link.href, window.location.href);
        if (link.matches("[data-chip]")) {
          link.closest("[data-type-chips]")?.querySelectorAll("[data-chip]").forEach((chip => chip.classList.toggle("is-on", chip === link)));
          new URLSearchParams(window.location.search).forEach(((value, key) => {
            if (key === "filter.p.product_type" || key === "page" || key === "sort_by") return;
            url.searchParams.append(key, value);
          }));
          const sort = this.root.querySelector("[data-sort-select]");
          if (sort?.value) url.searchParams.set("sort_by", sort.value);
        }
        this.render(url.pathname + url.search);
        return;
      }
      const more = event.target.closest("[data-load-more]");
      if (more) {
        event.preventDefault();
        if (this.stream) this.loadStream(more); else this.loadMore(more);
        return;
      }
      const density = event.target.closest("[data-density]");
      if (density && density.closest(".toolbar__density")) {
        this.applyDensity(density.dataset.density);
        window.Vernika?.store?.set(DENSITY_KEY, density.dataset.density);
        return;
      }
      const expand = event.target.closest("[data-facet-more]");
      if (expand) {
        const list = expand.previousElementSibling;
        const open = list.classList.toggle("is-expanded");
        expand.textContent = open ? expand.dataset.labelLess : expand.dataset.labelMore;
      }
    }
    applyDensity(value) {
      const density = value === "comfy" ? "comfy" : "compact";
      const main = this.root.querySelector(".listing__main");
      if (main) main.dataset.density = density;
      this.root.querySelectorAll(".toolbar__density [data-density]").forEach((button => {
        button.setAttribute("aria-pressed", String(button.dataset.density === density));
      }));
    }
    queue(form, delay) {
      clearTimeout(this.timer);
      this.timer = setTimeout((() => this.render(this.paramsFrom(form))), delay);
    }
    paramsFrom(form) {
      const source = form || this.root.querySelector("[data-facets-form]");
      const params = source ? new URLSearchParams(new FormData(source)) : new URLSearchParams(window.location.search);
      for (const [key, value] of [ ...params.entries() ]) {
        if (value === "") params.delete(key);
      }
      const sort = this.root.querySelector("[data-sort-select]");
      if (sort) params.set("sort_by", sort.value);
      const terms = new URLSearchParams(window.location.search).get("q");
      if (terms && !params.has("q")) params.set("q", terms);
      params.delete("page");
      const query = params.toString();
      return `${window.location.pathname}${query ? `?${query}` : ""}`;
    }
    async fetchSection(target) {
      this.controller?.abort();
      this.controller = new AbortController;
      const url = new URL(target, window.location.origin);
      url.searchParams.set("section_id", this.sectionId);
      const response = await fetch(url, {
        signal: this.controller.signal
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return (new DOMParser).parseFromString(await response.text(), "text/html");
    }
    async render(target, push = true) {
      const busy = [ this.root, ...this.extras() ];
      busy.forEach((el => el.setAttribute("aria-busy", "true")));
      try {
        const doc = await this.fetchSection(target);
        this.swap(doc);
        if (push) history.pushState({}, "", target);
        const sort = this.root.querySelector("[data-sort-select]");
        const sortBy = new URL(target, window.location.origin).searchParams.get("sort_by");
        if (sort) {
          const fallback = [ ...sort.options ].some((o => o.value === sort.dataset.default)) ? sort.dataset.default : sort.options[0]?.value;
          sort.value = sortBy || fallback || sort.value;
        }
        this.markSort?.(sort?.value);
        this.syncChips();
        const top = this.root.getBoundingClientRect().top;
        if (top < 0) window.scrollBy({
          top: top - 140,
          behavior: "smooth"
        });
      } catch (error) {
        if (error.name !== "AbortError") window.location.href = target;
      } finally {
        busy.forEach((el => el.removeAttribute("aria-busy")));
      }
    }
    swap(doc) {
      const openFacets = new Set([ ...document.querySelectorAll(".facets--drawer .facet[open]") ].map((d => d.dataset.facet)));
      const expanded = new Set([ ...this.root.querySelectorAll("[data-facet-list].is-expanded") ].map((l => l.closest(".facet")?.dataset.facet)));
      const focusedId = document.activeElement?.id;
      const drawerBody = document.querySelector(`#filters-${this.sectionId} .drawer__body`);
      const drawerScroll = drawerBody?.scrollTop;
      document.querySelectorAll(`[data-region]`).forEach((region => {
        if (!this.owns(region)) return;
        const fresh = doc.querySelector(`[data-region="${region.dataset.region}"]`);
        if (!fresh) return;
        if (region.matches("form, [data-region-inner]")) {
          region.innerHTML = fresh.innerHTML;
        } else {
          region.replaceWith(document.importNode(fresh, true));
        }
      }));
      document.querySelectorAll(".facets--drawer .facet").forEach((facet => {
        if (openFacets.has(facet.dataset.facet)) facet.open = true;
      }));
      expanded.forEach((id => {
        const list = this.root.querySelector(`.facet[data-facet="${id}"] [data-facet-list]`);
        if (list) {
          list.classList.add("is-expanded");
          const button = list.nextElementSibling;
          if (button?.dataset.labelLess) button.textContent = button.dataset.labelLess;
        }
      }));
      if (drawerBody) document.querySelector(`#filters-${this.sectionId} .drawer__body`).scrollTop = drawerScroll;
      if (focusedId) document.getElementById(focusedId)?.focus({
        preventScroll: true
      });
      this.applyDensity(this.root.querySelector(".listing__main")?.dataset.density);
      this.initStream();
      document.dispatchEvent(new CustomEvent("listing:updated"));
      window.Vernika?.initReveal?.(this.root);
    }
    owns(region) {
      return this.root.contains(region) || Boolean(region.closest(`#filters-${this.sectionId}`));
    }
    async loadMore(link) {
      link.setAttribute("aria-busy", "true");
      try {
        const next = new URL(link.href, window.location.href);
        const doc = await this.fetchSection(next.pathname + next.search);
        const grid = this.root.querySelector('[data-region="grid"] .product-grid');
        const freshItems = doc.querySelectorAll('[data-region="grid"] .product-grid > li');
        const firstNew = freshItems[0];
        freshItems.forEach((item => grid.append(document.importNode(item, true))));
        const pagination = this.root.querySelector('[data-region="pagination"]');
        const freshPagination = doc.querySelector('[data-region="pagination"]');
        if (pagination && freshPagination) pagination.replaceWith(document.importNode(freshPagination, true));
        window.Vernika?.initReveal?.(this.root);
        document.dispatchEvent(new CustomEvent("listing:updated"));
        if (firstNew) {
          const appended = grid.querySelector(`li:nth-child(${grid.children.length - freshItems.length + 1}) a`);
          appended?.focus({
            preventScroll: true
          });
        }
      } catch {
        window.location.href = link.href;
      }
    }
  }
  const init = () => document.querySelectorAll("[data-listing]:not([data-ready])").forEach((root => {
    root.dataset.ready = "";
    new Listing(root);
  }));
  init();
  document.addEventListener("shopify:section:load", init);
})();
